/**
 * DiagnosticPage — OCM Complexity Diagnostic, marketing version
 * Route: /diagnostic (public, no auth required)
 *
 * Flow:
 *   1. User checks applicable change triggers
 *   2. User selects 3 dropdown context inputs
 *   3. Live score bar updates in real time
 *   4. On submit → POST /diagnostic/submit → renders result card
 */

import { useState, useMemo } from 'react'
import {
  TRIGGER_ITEMS,
  MARKETING_DROPDOWNS,
  TIER_META,
  scoreMarketing,
  type TierKey,
} from '../data/diagnostic-items'
import styles from './DiagnosticPage.module.css'

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://clutch-ocm-scorer-uqrk7imnlq-uc.a.run.app'

// ── Types ─────────────────────────────────────────────────────────────────────

interface DiagnosticResult {
  diagnostic_run_id: number
  version: string
  trigger_score: number
  total_score: number
  xocm_tier: TierKey
  tier_label: string
  tier_short: string
  interpretation: string
  recommendations: string[]
  top_drivers: { key: string; label: string; score: number }[]
  created_at: string
}

type PageState = 'form' | 'submitting' | 'result' | 'error'

// ── Max score for the version (for bar fill calculation)
const MAX_SCORE = 17 // marketing: 8 triggers + 3 × 3 pts

// ── Component ─────────────────────────────────────────────────────────────────

export default function DiagnosticPage() {
  // Trigger checkboxes — map of index → boolean
  const [triggers, setTriggers] = useState<Record<number, boolean>>({})
  // Dropdown selections — map of key → value (int)
  const [inputs, setInputs] = useState<Record<string, number>>({})

  const [pageState, setPageState] = useState<PageState>('form')
  const [result, setResult] = useState<DiagnosticResult | null>(null)
  const [errorMsg, setErrorMsg] = useState('')

  // ── Live scoring
  const triggerCount = useMemo(() => Object.values(triggers).filter(Boolean).length, [triggers])
  const { total: liveTotal, tier: liveTier } = useMemo(
    () => scoreMarketing(triggerCount, inputs),
    [triggerCount, inputs]
  )
  const fillPct = Math.min(100, (liveTotal / MAX_SCORE) * 100)
  const allDropdownsFilled = MARKETING_DROPDOWNS.every(d => inputs[d.key] !== undefined)

  // ── Handlers
  function toggleTrigger(idx: number) {
    setTriggers(prev => ({ ...prev, [idx]: !prev[idx] }))
  }

  function setDropdown(key: string, value: number) {
    setInputs(prev => ({ ...prev, [key]: value }))
  }

  async function handleSubmit() {
    if (!allDropdownsFilled) return

    setPageState('submitting')
    setErrorMsg('')

    // Convert triggers to {index_string: bool} — matches Python expectations
    const triggerFlags: Record<string, boolean> = {}
    TRIGGER_ITEMS.forEach((_, idx) => {
      triggerFlags[String(idx)] = !!triggers[idx]
    })

    try {
      const res = await fetch(`${API_BASE}/diagnostic/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version: 'marketing',
          engagement_id: null,
          trigger_flags: triggerFlags,
          inputs,
        }),
      })

      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `Server error ${res.status}`)
      }

      const data: DiagnosticResult = await res.json()
      setResult(data)
      setPageState('result')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setErrorMsg(msg)
      setPageState('error')
    }
  }

  function handleReset() {
    setTriggers({})
    setInputs({})
    setResult(null)
    setPageState('form')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ── Render: Result ─────────────────────────────────────────────────────────

  if (pageState === 'result' && result) {
    const tier = result.xocm_tier as TierKey
    const meta = TIER_META[tier]
    const maxDriverScore = Math.max(...result.top_drivers.map(d => d.score), 1)

    return (
      <div className={styles.page}>
        <div className={styles.hero}>
          <p className={styles.heroEyebrow}>OCM Complexity Diagnostic</p>
          <h1 className={styles.heroTitle}>Your Results</h1>
          <p className={styles.heroSub}>
            Based on your inputs, here is the recommended OCM approach for this change initiative.
          </p>
        </div>

        <div className={styles.resultPage}>
          {/* Tier hero */}
          <div className={`${styles.resultHero} ${styles[meta.colorClass]}`}>
            <p className={styles.resultEyebrow}>xOCM Tier</p>
            <h2 className={styles.resultTierLabel}>{result.tier_label}</h2>
            <p className={styles.resultTierShort}>{result.tier_short}</p>
            <p className={styles.resultScoreLine}>
              Total score: {result.total_score} / {MAX_SCORE} &nbsp;·&nbsp; Triggers: {result.trigger_score} &nbsp;·&nbsp; Context inputs: {result.total_score - result.trigger_score}
            </p>
          </div>

          {/* Interpretation */}
          <div className={styles.resultCard}>
            <p className={styles.resultSectionTitle}>What this means</p>
            <p className={styles.resultInterpretation}>{result.interpretation}</p>
          </div>

          {/* Recommendations */}
          <div className={styles.resultCard}>
            <p className={styles.resultSectionTitle}>Recommended next steps</p>
            <ul className={styles.recommendationList}>
              {result.recommendations.map((rec, i) => (
                <li key={i}>{rec}</li>
              ))}
            </ul>
          </div>

          {/* Top drivers */}
          {result.top_drivers.length > 0 && (
            <div className={styles.resultCard}>
              <p className={styles.resultSectionTitle}>Top complexity drivers</p>
              <div className={styles.driversGrid}>
                {result.top_drivers.map(d => (
                  <div className={styles.driverRow} key={d.key}>
                    <span className={styles.driverLabel}>{d.label}</span>
                    <div className={styles.driverTrack}>
                      <div
                        className={styles.driverFill}
                        style={{ width: `${(d.score / maxDriverScore) * 100}%` }}
                      />
                    </div>
                    <span className={styles.driverScore}>{d.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.resetWrap}>
            <button className={styles.resetBtn} onClick={handleReset}>
              Start a new assessment
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Render: Form ────────────────────────────────────────────────────────────

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <p className={styles.heroEyebrow}>OCM Complexity Diagnostic</p>
        <h1 className={styles.heroTitle}>How complex is your change initiative?</h1>
        <p className={styles.heroSub}>
          Answer a few questions about your initiative to get an evidence-based recommendation
          for the level of OCM support your organization needs.
        </p>
      </div>

      <div className={styles.formWrap}>
        {/* Triggers */}
        <div className={styles.card}>
          <p className={styles.cardTitle}>Step 1 — Change triggers</p>
          <p className={styles.cardDesc}>
            Check every statement that applies to this initiative. Each checked item adds 1 point.
          </p>
          <div className={styles.triggerGrid}>
            {TRIGGER_ITEMS.map((text, idx) => (
              <label
                key={idx}
                className={`${styles.triggerRow} ${triggers[idx] ? styles.checked : ''}`}
              >
                <input
                  type="checkbox"
                  className={styles.triggerCheckbox}
                  checked={!!triggers[idx]}
                  onChange={() => toggleTrigger(idx)}
                />
                <span className={styles.triggerLabel}>{text}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Dropdowns */}
        <div className={styles.card}>
          <p className={styles.cardTitle}>Step 2 — Change context</p>
          <p className={styles.cardDesc}>
            Select the option that best describes the impact and scope of this initiative.
            All three fields are required.
          </p>
          <div className={styles.dropdownList}>
            {MARKETING_DROPDOWNS.map(item => (
              <div key={item.key} className={styles.dropdownItem}>
                <p className={styles.dropdownLabel}>{item.label}</p>
                <select
                  className={`${styles.dropdownSelect} ${inputs[item.key] !== undefined ? styles.filled : ''}`}
                  value={inputs[item.key] !== undefined ? String(inputs[item.key]) : ''}
                  onChange={e => {
                    const v = e.target.value
                    if (v !== '') setDropdown(item.key, Number(v))
                  }}
                >
                  <option value="">Select one…</option>
                  {item.options.map(opt => (
                    <option key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Live score bar */}
        <div className={styles.scoreBarCard}>
          <span className={styles.scoreBarLabel}>Complexity score</span>
          <div className={styles.scoreBarTrack}>
            <div
              className={`${styles.scoreBarFill} ${styles[`tier${liveTier}`]}`}
              style={{ width: `${fillPct}%` }}
            />
          </div>
          <span className={styles.scoreNum}>{liveTotal}</span>
          <span className={`${styles.scoreTierBadge} ${styles[`tier${liveTier}`]}`}>
            {liveTier}
          </span>
        </div>

        {/* Error */}
        {pageState === 'error' && (
          <div className={styles.errorBanner}>
            Could not save results: {errorMsg}. Your score is still visible above — try submitting again.
          </div>
        )}

        {/* Submit */}
        <div className={styles.submitWrap}>
          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!allDropdownsFilled || pageState === 'submitting'}
          >
            {pageState === 'submitting' ? 'Scoring…' : 'Get my recommendation →'}
          </button>
        </div>
      </div>
    </div>
  )
}
